// RGR01 엑셀 업로드 — 1.0 TOOL_IMP 파리티(한글 헤더 → 컬럼 매칭 클라 파싱 → 표 반영 → [저장]).
// 서버 업로드 API 는 1.0 에도 2.0 에도 없다. 저장은 사용자가 검토한 뒤 request-inbound 로 나간다.
import ExcelJS from 'exceljs'
import { cellText } from '@/pages/inbound/generalInbound/components/excelImport'

export type ImportedRow = Record<string, string | number>

/** 화면이 만든 파싱 오류 — 문구를 그대로 보여 준다. 그 밖의 예외(깨진 파일의 라이브러리 오류 등)는 화면이 고정 문구로 바꾼다. */
export class ExcelImportError extends Error {}

/** 표시명·원장 코드 → 원장 코드. 못 되돌리면 undefined. 빈 코드('')가 온라인·양품이다 */
export type CodeMapper = (text: string) => string | undefined

export const makeCodeMapper = (options: { value: string; label: string }[]): CodeMapper => {
  const byLabel = new Map(options.map((o) => [o.label, o.value]))
  const byValue = new Set(options.map((o) => o.value))
  return (text) => byLabel.get(text) ?? (byValue.has(text) ? text : text.toUpperCase() === 'NULL' ? '' : undefined)
}

/** 칸이 비면 키를 빼지 않고 빈 문자열을 넣어야 하는 숫자 칸 — 이유는 아래 파서 안 주석 참조 */
const NUMERIC_KEYS = new Set(['ORD_QTY'])

/** 'yyyy-MM-dd' 로 정규화 — 자릿수만이 아니라 실제 달력에 있는 날인지까지 본다(GR01 normalizeDate 와 같은 규칙). 못 읽으면 null */
const normalizeDate = (text: string): string | null => {
  const digits = text.replace(/[^0-9]/g, '')
  if (digits.length !== 8) return null
  const [year, month, day] = [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)].map(Number)
  const parsed = new Date(year, month - 1, day)
  const sameDay = year >= 2000 && year <= 2100 && parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day
  return sameDay ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}` : null
}

const rowsSummary = (numbers: number[]) => `${numbers.slice(0, 5).join(', ')}행${numbers.length > 5 ? ` 외 ${numbers.length - 5}개 행` : ''}`

/**
 * 첫 시트 1행을 한글 헤더로 읽어 `titleToKey` 로 컬럼 키를 찾는다. 모르는 헤더는 버린다.
 * 코드 컬럼(`codeMappers`)은 표시명·원장 코드 둘 다 받고, 못 읽는 행이 있으면 행 번호를 모아 한 번에 거절한다.
 */
export const parseReturnInboundExcel = async (
  file: File,
  { titleToKey, codeMappers }: { titleToKey: Map<string, string>; codeMappers: Record<string, CodeMapper> },
): Promise<ImportedRow[]> => {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  const ws = wb.worksheets[0]
  if (!ws) return []

  const headerKeys: (string | null)[] = []
  ws.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
    headerKeys[col] = titleToKey.get(cellText(cell.value).trim()) ?? null
  })
  if (!headerKeys.some(Boolean)) throw new ExcelImportError('헤더를 인식할 수 없습니다. 엑셀 다운로드 파일과 같은 1행 헤더가 필요합니다.')

  const rows: ImportedRow[] = []
  const badRows: Record<string, number[]> = {}
  const markBad = (key: string, rowNumber: number) => {
    badRows[key] = [...(badRows[key] ?? []), rowNumber]
  }
  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const item: ImportedRow = {}
    let hasValue = false
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headerKeys[col]
      if (!key) return
      const text = cellText(cell.value).trim()
      if (!text) {
        // 숫자 칸이 비면 키를 빼지 않고 빈 문자열을 명시로 넣는다. 키가 없으면 신규 행 기본값이 그대로 남는데,
        // 그 기본값이 0 이 되는 순간 isBlank(0) 이 false 라 필수값 검사를 그냥 통과한다(GR01 excelImport.ts 선례).
        if (NUMERIC_KEYS.has(key)) item[key] = ''
        return
      }
      hasValue = true
      if (key === 'ORD_QTY') {
        // 숫자로 못 읽으면 원문을 둔다 — 저장 전 검사가 "수량이 숫자가 아닙니다" 로 짚는다
        const parsed = Number(text.replaceAll(',', ''))
        item[key] = Number.isNaN(parsed) ? text : parsed
      } else if (key === 'PLN_DATE') {
        const normalized = normalizeDate(text)
        if (!normalized) markBad(key, rowNumber)
        item[key] = normalized ?? text
      } else if (codeMappers[key]) {
        const mapped = codeMappers[key](text)
        if (mapped === undefined) markBad(key, rowNumber)
        else item[key] = mapped
      } else {
        item[key] = text
      }
    })
    if (hasValue) rows.push(item)
  })

  const keyToTitle = new Map([...titleToKey].map(([title, key]) => [key, title]))
  const errors = Object.entries(badRows).map(([key, numbers]) => `${keyToTitle.get(key) ?? key}을(를) 읽을 수 없는 행이 있습니다 (${rowsSummary(numbers)}).`)
  // message.error 는 단일 줄 토스트다 — 공백으로 이어 붙인다
  if (errors.length) throw new ExcelImportError(errors.join(' '))
  return rows
}
